import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  Assignment as AssignmentIcon,
  Timeline as TimelineIcon,
  AccountCircle,
  Logout,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Business as CompanyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';

const drawerWidth = 240;

interface MainLayoutProps {
  children: React.ReactNode;
}

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badge?: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  const handleProfileView = () => {
    setProfileDialogOpen(true);
    handleProfileMenuClose();
  };

  const handleProfileDialogClose = () => {
    setProfileDialogOpen(false);
  };

  const menuItems: NavigationItem[] = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Companies', icon: <BusinessIcon />, path: '/companies' },
    { text: 'Positions', icon: <WorkIcon />, path: '/positions' },
    { text: 'Potential Candidates', icon: <PeopleIcon />, path: '/potential-candidates' },
    { text: 'Candidates', icon: <PeopleIcon />, path: '/candidates' },
    { text: 'Interviews', icon: <EventIcon />, path: '/interviews' },
    { text: 'Daily Reports', icon: <AssignmentIcon />, path: '/reports/daily' },
    { text: 'Candidate Updates', icon: <TimelineIcon />, path: '/reports/candidate-updates' },
    { text: 'Email Center', icon: <EmailIcon />, path: '/email-center' },
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => {
    if (user?.userType === 'Candidate') {
      return ['Dashboard', 'Positions', 'Interviews'].includes(item.text);
    }
    if (user?.userType === 'Hiring Manager') {
      return ['Dashboard', 'Candidates', 'Potential Candidates', 'Positions', 'Companies', 'Interviews', 'Daily Reports', 'Candidate Updates', 'Email Center'].includes(item.text);
    }
    return true; // M4 Consultant and Admin see all
  });

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" color="primary" fontWeight="bold">
          M4 Consulting ATS
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {filteredMenuItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
          >
            <ListItemButton
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.light + '20',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light + '30',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  color: location.pathname === item.path ? theme.palette.primary.main : 'inherit',
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  color="warning"
                  sx={{
                    height: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1 }}>
        <AppBar
          position="fixed"
          sx={{
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${drawerWidth}px` },
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {filteredMenuItems.find(item => item.path === location.pathname)?.text || 'M4 Consulting ATS'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32 }}>
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          component="nav"
          sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
          aria-label="mailbox folders"
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
        >
          <MenuItem onClick={handleProfileView}>
            <ListItemIcon>
              <AccountCircle fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <Logout fontSize="small" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Toolbar />
          <Box sx={{ flex: 1 }}>
            {children}
          </Box>
        </Box>
      </Box>

      <Footer />

      {/* Profile Dialog */}
      <Dialog
        open={profileDialogOpen}
        onClose={handleProfileDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700
        }}>
          User Profile
          <IconButton
            onClick={handleProfileDialogClose}
            sx={{ color: '#60a5fa' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {user && (
            <Box>
              {/* User Avatar and Name */}
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mr: 3,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    fontSize: '2rem',
                    fontWeight: 600
                  }}
                >
                  {user.firstName?.[0]}{user.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Chip
                    label={user.userType}
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>

              {/* Contact Information */}
              <Box mb={2}>
                <Typography variant="h6" sx={{ color: '#60a5fa', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <EmailIcon sx={{ mr: 1 }} />
                  Contact Information
                </Typography>

                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon sx={{ mr: 2, color: '#60a5fa', fontSize: '1.2rem' }} />
                  <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {user.email}
                  </Typography>
                </Box>

                {user.phone && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <PhoneIcon sx={{ mr: 2, color: '#60a5fa', fontSize: '1.2rem' }} />
                    <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      {user.phone}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Professional Information */}
              <Box mb={2}>
                <Typography variant="h6" sx={{ color: '#60a5fa', mb: 2, display: 'flex', alignItems: 'center' }}>
                  <BadgeIcon sx={{ mr: 1 }} />
                  Professional Details
                </Typography>

                <Box display="flex" alignItems="center" mb={1}>
                  <BadgeIcon sx={{ mr: 2, color: '#60a5fa', fontSize: '1.2rem' }} />
                  <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    Role: {user.userType}
                  </Typography>
                </Box>

                {user.department && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <CompanyIcon sx={{ mr: 2, color: '#60a5fa', fontSize: '1.2rem' }} />
                    <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      Department: {user.department}
                    </Typography>
                  </Box>
                )}

                {user.jobTitle && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <BadgeIcon sx={{ mr: 2, color: '#60a5fa', fontSize: '1.2rem' }} />
                    <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      Job Title: {user.jobTitle}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" mb={1}>
                  <AccountCircle sx={{ mr: 2, color: '#60a5fa', fontSize: '1.2rem' }} />
                  <Typography component="span" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    Status: {user.active ? 'Active' : 'Inactive'}
                  </Typography>
                </Box>

                {user.lastLogin && (
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography component="span" variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Last Login: {user.lastLogin.toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {user.notes && (
                <Box>
                  <Typography variant="h6" sx={{ color: '#60a5fa', mb: 1 }}>
                    Notes
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {user.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleProfileDialogClose}
            variant="outlined"
            sx={{
              borderColor: 'rgba(96, 165, 250, 0.5)',
              color: '#60a5fa',
              '&:hover': {
                borderColor: '#60a5fa',
                backgroundColor: 'rgba(96, 165, 250, 0.1)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MainLayout;